import React from 'react';

const inline = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i} className="text-foreground font-semibold">{p.slice(2, -2)}</strong>;
    if (p.startsWith('*') && p.endsWith('*') && p.length > 2) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={i} className="px-1 py-0.5 rounded bg-muted text-[12px] font-mono">{p.slice(1, -1)}</code>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

const cells = (line) => line.split('|').slice(1, -1).map(c => c.trim());

export const KbMarkdown = ({ text = '' }) => {
  const lines = String(text).split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim().startsWith('|')) {
      const table = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) { table.push(lines[i]); i += 1; }
      const header = cells(table[0]);
      const rows = table.slice(table[1] && /^\s*\|[\s:-]+\|/.test(table[1]) ? 2 : 1).map(cells);
      blocks.push(
        <div key={blocks.length} className="overflow-x-auto my-4">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-muted/50">
                {header.map((h, hi) => (
                  <th key={hi} className="text-left p-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b">{inline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-b last:border-0 align-top">
                  {r.map((c, ci) => <td key={ci} className="p-2.5 text-muted-foreground">{inline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (/^#{2,4}\s/.test(line)) {
      const level = line.match(/^#+/)[0].length;
      const content = line.replace(/^#+\s/, '');
      blocks.push(
        <h3 key={blocks.length} className={level === 2
          ? 'text-base md:text-lg font-semibold text-foreground mt-6 mb-2'
          : 'text-sm font-semibold text-foreground mt-4 mb-1.5'}>
          {inline(content)}
        </h3>
      );
      i += 1;
      continue;
    }

    if (/^\s*[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && lines[i].trim() !== '') {
        if (/^\s*[-*]\s/.test(lines[i])) items.push(lines[i].replace(/^\s*[-*]\s/, ''));
        else if (items.length) items[items.length - 1] += ` ${lines[i].trim()}`;
        else break;
        i += 1;
      }
      blocks.push(
        <ul key={blocks.length} className="list-disc pl-5 space-y-1 my-3 text-sm text-muted-foreground">
          {items.map((it, ii) => <li key={ii}>{inline(it)}</li>)}
        </ul>
      );
      continue;
    }

    if (/^\s*\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && lines[i].trim() !== '') {
        if (/^\s*\d+\.\s/.test(lines[i])) items.push(lines[i].replace(/^\s*\d+\.\s/, ''));
        else if (items.length && !/^(#{2,4}\s|\|)/.test(lines[i])) {
          items[items.length - 1] += ` ${lines[i].trim().replace(/^[-*]\s/, '· ')}`;
        }
        else break;
        i += 1;
      }
      blocks.push(
        <ol key={blocks.length} className="list-decimal pl-5 space-y-1.5 my-3 text-sm text-muted-foreground">
          {items.map((it, ii) => <li key={ii}>{inline(it)}</li>)}
        </ol>
      );
      continue;
    }

    if (line.trim() === '') { i += 1; continue; }

    const para = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{2,4}\s|\s*[-*]\s|\s*\d+\.\s|\|)/.test(lines[i])) {
      para.push(lines[i]); i += 1;
    }
    blocks.push(<p key={blocks.length} className="text-sm text-muted-foreground leading-relaxed my-3">{inline(para.join(' '))}</p>);
  }

  return <div data-testid="kb-markdown">{blocks}</div>;
};

export default KbMarkdown;
